import { DISCOUNT_TYPES } from "constants/coupons";
import {
  normalizeTenantIdList,
  resolveCurrencyCodeFromOptions,
  resolveTenantCurrencyIds,
} from "helpers/couponScopeOptions";

export const normalizeDiscountType = (discountType) => {
  const normalizedType = String(discountType ?? "").trim().toLowerCase();

  if (normalizedType === DISCOUNT_TYPES.FLAT) {
    return DISCOUNT_TYPES.FLAT;
  }

  if (normalizedType === DISCOUNT_TYPES.PERCENTAGE) {
    return DISCOUNT_TYPES.PERCENTAGE;
  }

  return normalizedType;
};

export const isFlatDiscount = (discountType) =>
  normalizeDiscountType(discountType) === DISCOUNT_TYPES.FLAT;

export const isPercentageDiscount = (discountType) =>
  normalizeDiscountType(discountType) === DISCOUNT_TYPES.PERCENTAGE;

export const hasSelectedDiscountType = (discountType) =>
  normalizeDiscountType(discountType) !== "";

export const createEmptyCurrencySettingsEntry = () => ({
  discountAmount: "",
  maxAllowedAmount: "",
  minimumPackageMrp: "",
  minimumCartMrp: "",
});

export const syncCurrencySettings = (previousSettings, currencyIds) => {
  const nextSettings = {};
  const normalizedCurrencyIds = [
    ...new Set(
      currencyIds
        .map((currencyId) => String(currencyId ?? "").trim())
        .filter((currencyId) => currencyId.length > 0)
    ),
  ];

  normalizedCurrencyIds.forEach((currencyId) => {
    nextSettings[currencyId] = {
      ...createEmptyCurrencySettingsEntry(),
      ...(previousSettings?.[currencyId] ?? {}),
    };
  });

  return nextSettings;
};

export const getSelectedCurrencyIdsFromTenants = (tenants, tenantIds) => {
  const normalizedTenantIds = new Set(normalizeTenantIdList(tenantIds));

  const currencyIds = tenants
    .filter((tenant) => normalizedTenantIds.has(String(tenant.id)))
    .flatMap((tenant) => resolveTenantCurrencyIds(tenant));

  return [...new Set(currencyIds)];
};

export const getCurrencyCode = resolveCurrencyCodeFromOptions;

export const getCurrencyLabel = (currencyOptions, currencyId) => {
  if (
    currencyId === "" ||
    currencyId === null ||
    currencyId === undefined
  ) {
    return "";
  }

  const matchingCurrency = currencyOptions.find(
    (option) => String(option.id) === String(currencyId)
  );

  if (!matchingCurrency) {
    return String(currencyId);
  }

  return matchingCurrency.label ?? String(currencyId);
};

export const getCurrencyBlockTitle = (currencyOptions, currencyId) => {
  const matchingCurrency = currencyOptions.find(
    (option) => String(option.id) === String(currencyId)
  );

  if (!matchingCurrency) {
    return String(currencyId);
  }

  const name = String(matchingCurrency.name ?? "").trim();
  const label = String(matchingCurrency.label ?? "").trim();
  const code = String(matchingCurrency.code ?? "").trim();

  if (name !== "" && name !== String(currencyId)) {
    return name;
  }

  if (label !== "" && label !== String(currencyId)) {
    return label;
  }

  if (code !== "") {
    return code;
  }

  return String(currencyId);
};

export const formatAmountWithCurrencyPrefix = (
  value,
  currencyOptions,
  currencyId
) => {
  if (value === "" || value === null || value === undefined) {
    return "";
  }

  const currencyCode = getCurrencyCode(currencyOptions, currencyId);
  return currencyCode === "" ? String(value) : `${currencyCode} ${value}`;
};
